import { useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import type { SentenceToken } from "../game/types";
import { iconKey } from "../game/types";
import IconCell from "./IconCell";

interface Props {
  tokens: SentenceToken[];
  onClose: () => void;
}

/**
 * "Read as a sentence" popup — Cesco's idea. Instead of hunting for the framed
 * icons spread across the eight boards, this lays the code's icons out left to
 * right in reading order (1 → 2 → 3 …), like a sentence, keeping each icon's
 * frame colour, order number and tense/gender/negation tokens. It's a reading
 * aid only — it never shows the translation/solution.
 */
export default function SentenceView({ tokens, onClose }: Props) {
  const { t } = useTranslation();
  const sorted = [...tokens].sort((a, b) => a.order - b.order);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <div className="zoom-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div className="zoom-card sentence-view" onClick={(e) => e.stopPropagation()}>
        <button className="zoom-close" onClick={onClose} aria-label="Close">×</button>
        <h3 style={{ margin: 0 }}>{t("sentenceView.title")}</h3>
        <p className="sentence-view__hint">{t("sentenceView.hint")}</p>
        <div className="sentence-view__strip">
          {sorted.map((tok, i) => (
            <div className="sentence-view__group" key={`${iconKey(tok.icon)}-${i}`}>
              <div className="sentence-view__cell">
                <IconCell cellRef={tok.icon} token={tok} />
              </div>
              {i < sorted.length - 1 && (
                <span className="sentence-view__arrow" aria-hidden>
                  {sorted[i].compoundWithNext ? "+" : "→"}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  );
}
